'use client';

import { useState, useEffect, useRef } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import type { NumberFormatType } from '@/lib/platform/utils/locale-formatting';
import { useCurrency } from '@/lib/platform/contexts/currency-context';
import { getCurrencySymbol } from '@/lib/platform/utils/currency';
import { NumberInput } from '@/components/platform/forms';
import { SocialPlatform, PlatformMetrics } from '@/lib/products/valuations/social-deepdive/types';
import { useAllCPMSuggestions } from '@/lib/hooks/use-cpm-suggestion';

interface SocialPlatformCardProps {
  platform: SocialPlatform;
  metrics: PlatformMetrics;
  onChange: (metrics: PlatformMetrics) => void;
  disabled?: boolean;
  numberFormat?: NumberFormatType;
}

export default function SocialPlatformCard({
  platform,
  metrics,
  onChange,
  disabled = false,
  numberFormat = 'eu',
}: SocialPlatformCardProps) {
  const { darkMode } = useDarkMode();
  const { baseCurrency } = useCurrency();
  const [isExpanded, setIsExpanded] = useState(false);

  const currencySymbol = getCurrencySymbol(baseCurrency);

  // Get smart CPM suggestions for this platform (with user's currency)
  const cpmSuggestions = useAllCPMSuggestions(platform.id, {
    currency: baseCurrency,
  });

  // Track if CPM fields have been manually touched by user
  const hasSetFollowersCpm = useRef(metrics.followers_cpm > 0);
  const hasSetImpressionsCpm = useRef(metrics.impressions_cpm > 0);
  const hasSetEngagementsCpm = useRef(metrics.engagements_cpm > 0);

  // Auto-populate CPM values with Smart suggestions when:
  // 1. User has entered volume (followers/impressions/engagements > 0)
  // 2. CPM is still 0 (user hasn't touched it)
  // 3. Smart suggestion is available
  useEffect(() => {
    const updated: Partial<PlatformMetrics> = {};
    let hasChanges = false;

    // Followers CPM
    if (
      metrics.followers > 0 &&
      metrics.followers_cpm === 0 &&
      !hasSetFollowersCpm.current &&
      cpmSuggestions.followers?.suggestedCPM
    ) {
      updated.followers_cpm = cpmSuggestions.followers.suggestedCPM;
      hasChanges = true;
    }

    // Impressions CPM
    if (
      metrics.impressions > 0 &&
      metrics.impressions_cpm === 0 &&
      !hasSetImpressionsCpm.current &&
      cpmSuggestions.impressions?.suggestedCPM
    ) {
      updated.impressions_cpm = cpmSuggestions.impressions.suggestedCPM;
      hasChanges = true;
    }

    // Engagements CPM
    if (
      metrics.engagements > 0 &&
      metrics.engagements_cpm === 0 &&
      !hasSetEngagementsCpm.current &&
      cpmSuggestions.engagements?.suggestedCPM
    ) {
      updated.engagements_cpm = cpmSuggestions.engagements.suggestedCPM;
      hasChanges = true;
    }

    if (hasChanges) {
      onChange({
        ...metrics,
        ...updated,
      });
    }
  }, [
    metrics,
    cpmSuggestions,
    onChange,
    hasSetFollowersCpm,
    hasSetImpressionsCpm,
    hasSetEngagementsCpm,
  ]);

  // Check if platform has any data
  const hasData =
    metrics.followers > 0 ||
    metrics.followers_cpm > 0 ||
    metrics.impressions > 0 ||
    metrics.impressions_cpm > 0 ||
    metrics.engagements > 0 ||
    metrics.engagements_cpm > 0;

  return (
    <div
      className={`overflow-hidden rounded-lg border ${
        darkMode ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white'
      }`}
    >
      {/* Header - Collapsible */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className={`flex w-full items-center justify-between p-4 transition-colors hover:bg-white/5 ${
          darkMode ? 'text-gray-100' : 'text-gray-900'
        }`}
      >
        <div className="flex items-center gap-3">
          <span style={{ color: platform.color }}>
            <platform.icon className="text-2xl" />
          </span>
          <div className="text-left">
            <h3 className="font-semibold">{platform.name}</h3>
            {hasData && !isExpanded && (
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {metrics.followers > 0 &&
                  `${metrics.followers.toLocaleString()} ${platform.followersTerm}`}
                {metrics.impressions > 0 && metrics.followers > 0 && ' • '}
                {metrics.impressions > 0 &&
                  `${metrics.impressions.toLocaleString()} ${platform.impressionsTerm}`}
                {metrics.engagements > 0 &&
                  (metrics.followers > 0 || metrics.impressions > 0) &&
                  ' • '}
                {metrics.engagements > 0 &&
                  `${metrics.engagements.toLocaleString()} ${platform.engagementsTerm}`}
              </p>
            )}
          </div>
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="h-5 w-5" />
        ) : (
          <ChevronDownIcon className="h-5 w-5" />
        )}
      </button>

      {/* Content - Expanded */}
      {isExpanded && (
        <div className="p-4 pt-0">
          <div className="grid grid-cols-2 gap-4">
            {/* Followers */}
            <div>
              <NumberInput
                label={platform.followersTerm}
                value={metrics.followers}
                onChange={(value) =>
                  onChange({
                    ...metrics,
                    followers: typeof value === 'number' ? value : 0,
                  })
                }
                decimals={0}
                numberFormat={numberFormat}
                placeholder="0"
                disabled={disabled}
              />
            </div>

            {/* Followers CPM */}
            <div>
              <NumberInput
                label={`${platform.followersTerm} CPM`}
                value={metrics.followers_cpm}
                onChange={(value) => {
                  hasSetFollowersCpm.current = true;
                  onChange({
                    ...metrics,
                    followers_cpm: typeof value === 'number' ? value : 0,
                  });
                }}
                decimals={2}
                numberFormat={numberFormat}
                suffix={currencySymbol}
                placeholder={
                  cpmSuggestions.followers ? `${cpmSuggestions.followers.suggestedCPM}` : '0'
                }
                helperText={
                  metrics.followers_cpm === 0 && cpmSuggestions.followers
                    ? `💡 Smart suggestion: ${cpmSuggestions.followers.suggestedCPM} ${cpmSuggestions.followers.currencySymbol} (${cpmSuggestions.followers.countryName} market)`
                    : undefined
                }
                disabled={disabled}
              />
            </div>

            {/* Impressions */}
            <div>
              <NumberInput
                label={platform.impressionsTerm}
                value={metrics.impressions}
                onChange={(value) =>
                  onChange({
                    ...metrics,
                    impressions: typeof value === 'number' ? value : 0,
                  })
                }
                decimals={0}
                numberFormat={numberFormat}
                placeholder="0"
                disabled={disabled}
              />
            </div>

            {/* Impressions CPM */}
            <div>
              <NumberInput
                label={`${platform.impressionsTerm} CPM`}
                value={metrics.impressions_cpm}
                onChange={(value) => {
                  hasSetImpressionsCpm.current = true;
                  onChange({
                    ...metrics,
                    impressions_cpm: typeof value === 'number' ? value : 0,
                  });
                }}
                decimals={2}
                numberFormat={numberFormat}
                suffix={currencySymbol}
                placeholder={
                  cpmSuggestions.impressions ? `${cpmSuggestions.impressions.suggestedCPM}` : '0'
                }
                helperText={
                  metrics.impressions_cpm === 0 && cpmSuggestions.impressions
                    ? `💡 Smart suggestion: ${cpmSuggestions.impressions.suggestedCPM} ${cpmSuggestions.impressions.currencySymbol} (${cpmSuggestions.impressions.countryName} market)`
                    : undefined
                }
                disabled={disabled}
              />
            </div>

            {/* Engagements */}
            <div>
              <NumberInput
                label={platform.engagementsTerm}
                value={metrics.engagements}
                onChange={(value) =>
                  onChange({
                    ...metrics,
                    engagements: typeof value === 'number' ? value : 0,
                  })
                }
                decimals={0}
                numberFormat={numberFormat}
                placeholder="0"
                disabled={disabled}
              />
            </div>

            {/* Engagements CPM */}
            <div>
              <NumberInput
                label={`${platform.engagementsTerm} CPM`}
                value={metrics.engagements_cpm}
                onChange={(value) => {
                  hasSetEngagementsCpm.current = true;
                  onChange({
                    ...metrics,
                    engagements_cpm: typeof value === 'number' ? value : 0,
                  });
                }}
                decimals={2}
                numberFormat={numberFormat}
                suffix={currencySymbol}
                placeholder={
                  cpmSuggestions.engagements ? `${cpmSuggestions.engagements.suggestedCPM}` : '0'
                }
                helperText={
                  metrics.engagements_cpm === 0 && cpmSuggestions.engagements
                    ? `💡 Smart suggestion: ${cpmSuggestions.engagements.suggestedCPM} ${cpmSuggestions.engagements.currencySymbol} (${cpmSuggestions.engagements.countryName} market)`
                    : undefined
                }
                disabled={disabled}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
